/**
 * Task dependency graph — topo order, critical path, slip detection.
 *
 * The PM core that layers dependencies (`ProjectTask.blockedBy`) over
 * the tasks collection. Everything here is PURE: functions take a
 * `ProjectTask[]` snapshot (plus an optional injected `today` for
 * determinism) and return structured results. No store access, no
 * throwing on bad data — cycles and dangling ids come back as flags so
 * a messy user-built graph can never crash a view.
 *
 * Consumers today: tests. Later: the auto-generated timeline / Gantt
 * atom and the deadline-watcher agent.
 */

import type { ProjectTask } from '../db/types';

const DAY_MS = 86_400_000;

/** Default "due soon" horizon for slip detection, in days. */
export const SLIP_HORIZON_DAYS = 3;

// ---------------------------------------------------------------------------
// Result shapes
// ---------------------------------------------------------------------------

export interface TopoResult {
  /** Task ids in dependency order — every blocker before its dependents. */
  order: string[];
  /** True when the graph contains at least one dependency cycle. */
  hasCycle: boolean;
  /** Ids of tasks caught in (or downstream-locked inside) a cycle. */
  cycleIds: string[];
}

export interface CriticalPathResult {
  /** Ids along the longest dependency chain, blockers first. */
  ids: string[];
  /** Total weight of the chain (chain length when weights default to 1). */
  length: number;
  /** Cycles are flagged, and the path is computed on the acyclic rest. */
  hasCycle: boolean;
}

export type SlipReasonKind =
  /** An incomplete blocker of this task is already past its due date. */
  | 'blocker-overdue'
  /** This task is due within the horizon but still has incomplete blockers. */
  | 'due-soon-blocked'
  /** This task is already overdue and still has incomplete blockers. */
  | 'overdue-blocked';

export interface SlipReason {
  kind: SlipReasonKind;
  /** The offending blocker, when the reason is about a specific one. */
  blockerId?: string;
  /** Human-readable explanation for badges / agent narration. */
  detail: string;
}

export interface TaskSlip {
  taskId: string;
  reasons: SlipReason[];
}

export interface TaskGraphAnalysis {
  topoOrder: string[];
  hasCycle: boolean;
  cycleIds: string[];
  criticalPath: CriticalPathResult;
  slips: TaskSlip[];
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/**
 * Resolve each task's blockers to ids that actually exist in the
 * snapshot. Dangling references (deleted tasks) are silently dropped —
 * a stale edge should never wedge the graph.
 */
function resolvedBlockers(tasks: ProjectTask[]): Map<string, string[]> {
  const ids = new Set(tasks.map((t) => t.id));
  const map = new Map<string, string[]>();
  for (const t of tasks) {
    map.set(
      t.id,
      (t.blockedBy ?? []).filter((b) => b !== t.id && ids.has(b)),
    );
  }
  return map;
}

/** Whole-day delta between an ISO date (YYYY-MM-DD) and `today`. */
function daysUntil(dueIso: string, todayIso: string): number | undefined {
  const due = Date.parse(`${dueIso}T00:00:00Z`);
  const today = Date.parse(`${todayIso}T00:00:00Z`);
  if (Number.isNaN(due) || Number.isNaN(today)) return undefined;
  return Math.round((due - today) / DAY_MS);
}

/** Local today as YYYY-MM-DD — the default when no `today` is injected. */
function localToday(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

const isDone = (t: ProjectTask): boolean => t.status === 'done';

// ---------------------------------------------------------------------------
// (a) Topological order + cycle detection (Kahn's algorithm — no throw)
// ---------------------------------------------------------------------------

export function topoSort(tasks: ProjectTask[]): TopoResult {
  const blockers = resolvedBlockers(tasks);

  // indegree = number of unresolved blockers; edges go blocker → dependent.
  const indegree = new Map<string, number>();
  const dependents = new Map<string, string[]>();
  for (const t of tasks) {
    indegree.set(t.id, blockers.get(t.id)!.length);
    for (const b of blockers.get(t.id)!) {
      const list = dependents.get(b) ?? [];
      list.push(t.id);
      dependents.set(b, list);
    }
  }

  // Stable seed: keep the caller's array order for ties.
  const queue = tasks.filter((t) => indegree.get(t.id) === 0).map((t) => t.id);
  const order: string[] = [];
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i];
    order.push(id);
    for (const dep of dependents.get(id) ?? []) {
      const n = indegree.get(dep)! - 1;
      indegree.set(dep, n);
      if (n === 0) queue.push(dep);
    }
  }

  const cycleIds = tasks.map((t) => t.id).filter((id) => !order.includes(id));
  return { order, hasCycle: cycleIds.length > 0, cycleIds };
}

// ---------------------------------------------------------------------------
// (b) Critical path — longest dependency chain (weighted; default weight 1)
// ---------------------------------------------------------------------------

/**
 * Longest chain through the dependency DAG. `weightOf` lets callers
 * plug in real estimates (hours, days) later; without it each task
 * counts 1, so `length` is the chain length. Tasks caught in a cycle
 * are excluded (flagged via `hasCycle`) and the path is computed on
 * the acyclic remainder — never a throw.
 */
export function criticalPath(
  tasks: ProjectTask[],
  weightOf: (task: ProjectTask) => number = () => 1,
): CriticalPathResult {
  const { order, hasCycle } = topoSort(tasks);
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const blockers = resolvedBlockers(tasks);
  const inOrder = new Set(order);

  // Longest weighted path ENDING at each node, walked in topo order so
  // every blocker's distance is final before its dependents read it.
  const dist = new Map<string, number>();
  const prev = new Map<string, string | null>();
  for (const id of order) {
    const w = Math.max(0, weightOf(byId.get(id)!));
    let best = 0;
    let bestPrev: string | null = null;
    for (const b of blockers.get(id)!) {
      if (!inOrder.has(b)) continue; // blocker trapped in a cycle
      const d = dist.get(b) ?? 0;
      if (d > best) {
        best = d;
        bestPrev = b;
      }
    }
    dist.set(id, best + w);
    prev.set(id, bestPrev);
  }

  // Reconstruct from the heaviest endpoint back through best blockers.
  let endId: string | null = null;
  let max = 0;
  for (const id of order) {
    const d = dist.get(id)!;
    if (d > max) {
      max = d;
      endId = id;
    }
  }
  const ids: string[] = [];
  for (let cur = endId; cur; cur = prev.get(cur) ?? null) ids.unshift(cur);

  return { ids, length: max, hasCycle };
}

// ---------------------------------------------------------------------------
// (c) Slip detection
// ---------------------------------------------------------------------------

/**
 * Tasks that realistically cannot land on time:
 *   - `blocker-overdue`: an incomplete blocker is already past due —
 *     the dependent inherits the lateness no matter its own due date.
 *   - `due-soon-blocked` / `overdue-blocked`: the task itself is due
 *     within `horizonDays` (or already overdue) while blockers are
 *     still incomplete.
 * Done tasks never slip; independent tasks never slip (an overdue task
 * with no dependencies is the plain "overdue" signal, not a slip).
 */
export function detectSlips(
  tasks: ProjectTask[],
  today: string = localToday(),
  horizonDays: number = SLIP_HORIZON_DAYS,
): TaskSlip[] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const blockers = resolvedBlockers(tasks);
  const slips: TaskSlip[] = [];

  for (const task of tasks) {
    if (isDone(task)) continue;
    const openBlockers = blockers
      .get(task.id)!
      .map((id) => byId.get(id)!)
      .filter((b) => !isDone(b));
    if (openBlockers.length === 0) continue;

    const reasons: SlipReason[] = [];

    for (const blocker of openBlockers) {
      if (!blocker.dueDate) continue;
      const d = daysUntil(blocker.dueDate, today);
      if (d !== undefined && d < 0) {
        reasons.push({
          kind: 'blocker-overdue',
          blockerId: blocker.id,
          detail: `Blocker "${blocker.title}" is ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} overdue and incomplete`,
        });
      }
    }

    if (task.dueDate) {
      const d = daysUntil(task.dueDate, today);
      if (d !== undefined && d < 0) {
        reasons.push({
          kind: 'overdue-blocked',
          detail: `Task is ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} overdue with ${openBlockers.length} incomplete blocker${openBlockers.length === 1 ? '' : 's'}`,
        });
      } else if (d !== undefined && d <= horizonDays) {
        reasons.push({
          kind: 'due-soon-blocked',
          detail: `Task is due in ${d} day${d === 1 ? '' : 's'} with ${openBlockers.length} incomplete blocker${openBlockers.length === 1 ? '' : 's'}`,
        });
      }
    }

    if (reasons.length > 0) slips.push({ taskId: task.id, reasons });
  }

  return slips;
}

// ---------------------------------------------------------------------------
// One-shot analysis
// ---------------------------------------------------------------------------

/** Full graph analysis in one pass: topo + cycles + critical path + slips. */
export function analyzeTaskGraph(
  tasks: ProjectTask[],
  today: string = localToday(),
  horizonDays: number = SLIP_HORIZON_DAYS,
): TaskGraphAnalysis {
  const topo = topoSort(tasks);
  return {
    topoOrder: topo.order,
    hasCycle: topo.hasCycle,
    cycleIds: topo.cycleIds,
    criticalPath: criticalPath(tasks),
    slips: detectSlips(tasks, today, horizonDays),
  };
}
